// Determine API URL based on environment
// - Development: Use VITE_API_URL or VITE_BFF_URL env var
// - Production: Use bff.opptora.ai (BFF transforms cookie -> Authorization header)
// - Preview: Use {env}.bff.opptora.ai based on current path
const getApiBaseUrl = () => {
  // Allow explicit override via environment variable
  if (import.meta.env.VITE_BFF_URL) {
    return import.meta.env.VITE_BFF_URL
  }
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }

  // Development mode - use direct API (for local testing without BFF)
  if (import.meta.env.DEV) {
    return 'https://feat-prg.api.opptora.ai'
  }

  // Production and preview - derive BFF URL from current hostname
  const hostname = window.location.hostname

  // Production: opptora.ai or www.opptora.ai -> bff.opptora.ai
  if (hostname === 'opptora.ai' || hostname === 'www.opptora.ai') {
    return 'https://bff.opptora.ai'
  }

  // Preview: preview.opptora.ai/{env}/ -> {env}.bff.opptora.ai
  if (hostname === 'preview.opptora.ai') {
    // Extract environment from URL path (e.g., /feat-prg/index.html)
    const pathParts = window.location.pathname.split('/').filter(Boolean)
    if (pathParts.length > 0 && pathParts[0] !== 'index.html') {
      return `https://${pathParts[0]}.bff.opptora.ai`
    }
  }

  // Fallback to production BFF
  return 'https://bff.opptora.ai'
}

const API_BASE_URL = getApiBaseUrl()
const MOCK_MODE = import.meta.env.VITE_MOCK_MODE === 'true' || (!navigator.onLine) || (import.meta.env.DEV && import.meta.env.VITE_MOCK_MODE !== 'false')

console.log('🔧 API Service Configuration:')
console.log('  - API_BASE_URL:', API_BASE_URL)
console.log('  - VITE_MOCK_MODE:', import.meta.env.VITE_MOCK_MODE)
console.log('  - navigator.onLine:', navigator.onLine)
console.log('  - import.meta.env.DEV:', import.meta.env.DEV)
console.log('  - import.meta.env.PROD:', import.meta.env.PROD)
console.log('  - MOCK_MODE:', MOCK_MODE)
console.log('  - Environment variables:', Object.keys(import.meta.env))

// Add a visual indicator on the page
if (MOCK_MODE) {
  console.warn('⚠️ RUNNING IN MOCK MODE - WHITEBOARD WILL NOT PERSIST ACROSS BROWSERS!')
} else {
  console.log('✅ RUNNING IN API MODE - WHITEBOARD WILL PERSIST ACROSS BROWSERS')
}

// Mock users for development
// Note: admin@levantar.ai grants admin access (see isAdminUser in App.jsx)
const MOCK_USERS = {
  'demo': 'demo123',
  'testuser': 'test123',
  'admin': 'admin123',
  'admin@levantar.ai': 'admin123'
}

// Mock ideas data for development
const MOCK_IDEAS = [
  // STRONG IDEAS (2 with high opportunity scores)
  {
    id: 'idea-1',
    idea_id: 'idea-1',
    what: 'AI-powered code review assistant that automatically detects bugs and suggests optimizations',
    pinned: true,
    pinned_at: '2025-01-15T10:30:00Z',
    who: 'Software development teams and independent developers struggling with code quality',
    why: 'Manual code reviews are slow and inconsistent. This AI assistant would catch 90% of common bugs before they reach production, saving teams 15+ hours per week.',
    what_expected: 'Reduce production bugs by 85%, speed up development cycles by 30%',
    make_money: true,
    positive_examples: 'GitHub Copilot for assistance, SonarQube for analysis, but with AI-powered suggestions',
    negative_examples: 'Traditional linters are too rigid, manual reviews are too slow',
    status: 'triage',
    team_members: ['Orin', 'Cleo', 'Finn', 'Una', 'Vera'],
    created_at: '2025-01-15T10:30:00Z',
    updated_at: '2025-01-15T14:20:00Z',
    whiteboard_data: null,
    triage_assessment: {
      is_well_formed: true,
      assessment_summary: 'This is a well-formed idea with clear value proposition',
      recommendation: 'Proceed to opportunity assessment',
      generated_at: '2025-01-15T11:00:00Z'
    },
    market_demand_evaluation: {
      score: 8.5,
      reason: 'High demand in growing software development market',
      generated_at: '2025-01-15T14:15:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    market_saturation_evaluation: {
      score: 7.2,
      reason: 'Competitive but room for innovation',
      generated_at: '2025-01-15T14:17:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    usp_evaluation: {
      score: 8.8,
      reason: 'AI-powered suggestions provide clear differentiation',
      generated_at: '2025-01-15T14:19:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    opportunity_assessment: {
      score: 8.2,
      reason: 'This idea has strong potential due to the growing demand for AI-powered development tools and the clear pain point of manual code review inefficiencies. The software development market is expanding rapidly, and while there is competition, the AI-powered suggestion feature provides meaningful differentiation. The target market (development teams) is well-defined and has budget for quality tools that can improve productivity and reduce bugs.',
      market_demand_score: 8.5,
      market_saturation_score: 7.2,
      usp_score: 8.8,
      generated_at: '2025-01-15T14:20:00Z',
      model_used: 'claude-3-7-sonnet'
    }
  },
  {
    id: 'idea-2',
    idea_id: 'idea-2',
    what: 'Virtual interior design platform with AR visualization and instant quotes',
    who: 'Homeowners planning renovations who want professional design help without high costs',
    why: 'Interior design is expensive and hard to visualize. This platform would make professional design accessible to everyone with AR previews and instant contractor quotes.',
    what_expected: 'Democratize interior design, reduce renovation decision anxiety by 70%',
    make_money: true,
    positive_examples: 'IKEA Place for AR, Houzz for inspiration, but with integrated design services',
    negative_examples: 'Traditional designers are too expensive, DIY apps lack professional guidance',
    status: 'completed',
    created_at: '2025-01-14T09:15:00Z',
    updated_at: '2025-01-14T12:30:00Z',
    whiteboard_data: null,
    market_demand_evaluation: {
      score: 7.8,
      reason: 'Strong demand in home improvement market',
      generated_at: '2025-01-14T12:25:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    market_saturation_evaluation: {
      score: 6.5,
      reason: 'Some competition but gaps in AR integration',
      generated_at: '2025-01-14T12:27:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    usp_evaluation: {
      score: 8.1,
      reason: 'AR visualization with instant quotes is unique',
      generated_at: '2025-01-14T12:29:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    opportunity_assessment: {
      score: 7.5,
      reason: 'Strong opportunity in the growing home renovation market. The combination of AR visualization with instant contractor quotes addresses a significant pain point for homeowners who struggle to visualize design changes and get pricing. While there are existing players like IKEA Place and Houzz, none offer the complete integrated experience of AR preview plus immediate professional quotes, creating a clear market gap.',
      market_demand_score: 7.8,
      market_saturation_score: 6.5,
      usp_score: 8.1,
      generated_at: '2025-01-14T12:30:00Z',
      model_used: 'claude-3-7-sonnet'
    }
  },
  
  // MODERATE IDEAS (2 with medium opportunity scores)
  {
    id: 'idea-3',
    idea_id: 'idea-3',
    what: 'Smart meal planning app that adapts to dietary restrictions and local grocery prices',
    pinned: true,
    pinned_at: '2025-01-13T14:20:00Z',
    who: 'Health-conscious families wanting to eat well while managing food budgets',
    why: 'Meal planning is time-consuming and expensive. This app would optimize nutrition and cost based on local prices and dietary needs.',
    what_expected: 'Save families 30% on grocery bills while improving nutrition',
    make_money: true,
    positive_examples: 'MyFitnessPal for tracking, Instacart for shopping, but with smart planning',
    negative_examples: 'Generic meal planners ignore local prices and preferences',
    status: 'completed',
    created_at: '2025-01-13T14:20:00Z',
    updated_at: '2025-01-13T16:45:00Z',
    whiteboard_data: null,
    market_demand_evaluation: {
      score: 6.2,
      reason: 'Moderate demand in health and food tech space',
      generated_at: '2025-01-13T16:40:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    market_saturation_evaluation: {
      score: 5.8,
      reason: 'Crowded market with many meal planning apps',
      generated_at: '2025-01-13T16:42:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    usp_evaluation: {
      score: 6.5,
      reason: 'Price optimization adds some differentiation',
      generated_at: '2025-01-13T16:44:00Z',
      model_used: 'claude-3-7-sonnet'
    },
    opportunity_assessment: {
      score: 6.2,
      reason: 'Moderate opportunity in the health and food tech space. While meal planning apps are common, the specific focus on local price optimization and dietary restrictions provides some differentiation. The market is crowded but there may be room for a more intelligent, cost-conscious solution that truly adapts to individual needs and local market conditions.',
      market_demand_score: 6.2,
      market_saturation_score: 5.8,
      usp_score: 6.5,
      generated_at: '2025-01-13T16:45:00Z',
      model_used: 'claude-3-7-sonnet'
    }
  },
  {
    id: 'idea-4',
    idea_id: 'idea-4',
    what: 'Professional networking platform specifically for remote workers and digital nomads',
    who: 'Remote workers and digital nomads seeking professional connections and collaboration',
    why: 'Traditional networking is location-based. Remote workers need a platform for global professional connections and collaboration opportunities.',
    what_expected: 'Increase remote work opportunities and professional connections by 50%',
    make_money: true,
    positive_examples: 'LinkedIn for networking, Nomad List for community, but focused on professional growth',
    negative_examples: 'LinkedIn is too broad, existing nomad platforms lack professional focus',
    status: 'completed',
    created_at: '2025-01-12T11:10:00Z',
    updated_at: '2025-01-12T15:20:00Z',
    whiteboard_data: null,
    opportunity_assessment: {
      market_demand: { score: 5.9, reasoning: 'Growing remote work trend but niche market' },
      market_saturation: { score: 6.1, reasoning: 'Some existing solutions but room for specialization' },
      usp: { score: 5.7, reasoning: 'Remote focus provides differentiation but limited appeal' },
      reason: 'Moderate opportunity driven by remote work growth, but market appeal may be limited. While remote work is expanding, the target audience of digital nomads is relatively small compared to broader professional networks. LinkedIn already serves most networking needs, and specialized nomad platforms exist. Success would depend on creating genuine professional value beyond just location-based connections.'
    }
  },

  // WEAK IDEAS (2 with low opportunity scores)
  {
    id: 'idea-5',
    idea_id: 'idea-5',
    what: 'Social media platform exclusively for sharing photos of breakfast foods',
    who: 'Food enthusiasts who love sharing their breakfast creations',
    why: 'Breakfast is the most important meal but gets less social media attention than dinner. This would create a dedicated community.',
    what_expected: 'Build a community of 100k breakfast enthusiasts',
    make_money: false,
    positive_examples: 'Instagram for photos, Untappd for beer, but focused on breakfast',
    negative_examples: 'Too niche compared to general food apps',
    status: 'completed',
    created_at: '2025-01-11T08:30:00Z',
    updated_at: '2025-01-11T09:15:00Z',
    whiteboard_data: null,
    opportunity_assessment: {
      score: 3.0,
      market_demand_score: 3.2,
      market_saturation_score: 4.1,
      usp_score: 2.8,
      reason: 'Weak opportunity due to extremely niche focus and existing solutions. The breakfast-only social media concept is too narrow to build a sustainable business or community. Instagram, food blogs, and general social platforms already serve this need effectively. The target market of breakfast enthusiasts is too small and the differentiation is insufficient to justify a standalone platform.',
      generated_at: '2025-01-11T09:15:00Z',
      model_used: 'claude-3-7-sonnet'
    }
  },
  {
    id: 'idea-6',
    idea_id: 'idea-6',
    what: 'App that reminds you to water your houseplants with plant-specific schedules',
    who: 'Houseplant owners who frequently forget to water their plants',
    why: 'Many people kill plants by forgetting to water them. This app would send personalized reminders based on plant type and season.',
    what_expected: 'Reduce plant mortality rates by 40% for users',
    make_money: false,
    positive_examples: 'Smartphone reminders, gardening apps, but plant-specific',
    negative_examples: 'Simple calendar reminders work just as well',
    status: 'completed',
    created_at: '2025-01-10T16:45:00Z',
    updated_at: '2025-01-10T17:30:00Z',
    whiteboard_data: null,
    opportunity_assessment: {
      score: 3.8,
      market_demand_score: 4.1,
      market_saturation_score: 3.8,
      usp_score: 3.5,
      reason: 'Weak opportunity with limited market potential and strong existing alternatives. While houseplant ownership has grown, the problem is easily solved by existing solutions including basic smartphone reminders, calendar apps, and numerous existing gardening apps. Plant-specific scheduling is not enough differentiation to justify a dedicated app when simple, free alternatives work effectively.',
      generated_at: '2025-01-10T17:30:00Z',
      model_used: 'claude-3-7-sonnet'
    }
  },

  // IDEAS IN REVIEW STATES (2 of each)
  {
    id: 'idea-7',
    idea_id: 'idea-7',
    what: 'Blockchain-based voting system for local government elections',
    who: 'Local government officials wanting to increase voter turnout and transparency',
    why: 'Traditional voting has low turnout and trust issues. Blockchain could increase participation through mobile voting while ensuring transparency.',
    what_expected: 'Increase local election turnout by 60%',
    make_money: true,
    positive_examples: 'Estonia\'s e-voting system, blockchain for transparency',
    negative_examples: 'Paper voting is slow and has low turnout',
    status: 'idea-refinement',
    created_at: '2025-01-09T13:20:00Z',
    updated_at: '2025-01-09T13:20:00Z',
    whiteboard_data: null,
    triage_assessment: {
      reasoning: 'The idea addresses an important problem but lacks critical technical and regulatory details needed for implementation',
      confidence: 0.8,
      refinement_questions: [
        {
          question: 'What specific blockchain technology would you use (e.g., Ethereum, Hyperledger), and how would it ensure both transparency and voter anonymity?',
          field: 'what',
          why: 'Technical implementation details are critical for blockchain voting systems, as they need to balance transparency with privacy'
        },
        {
          question: 'How would you address the digital divide to ensure elderly and low-tech voters can participate securely?',
          field: 'who',
          why: 'Understanding your complete target audience and accessibility requirements is essential for democratic voting systems'
        },
        {
          question: 'What specific regulatory approvals and legal frameworks would you need to navigate for blockchain-based voting?',
          field: 'what_expected',
          why: 'Legal and regulatory compliance is one of the biggest barriers to electronic voting adoption'
        }
      ]
    }
  },
  {
    id: 'idea-8',
    idea_id: 'idea-8',
    what: 'AI therapist chatbot for initial mental health screening and support',
    who: 'People seeking mental health support who face barriers to traditional therapy',
    why: 'Mental health services are expensive and have long wait times. An AI screening tool could provide immediate support and triage.',
    what_expected: 'Provide mental health support to 10x more people at lower cost',
    make_money: true,
    positive_examples: 'Woebot for mental health, ChatGPT for conversation',
    negative_examples: 'Traditional therapy is expensive and hard to access',
    status: 'idea-refinement',
    created_at: '2025-01-08T10:15:00Z',
    updated_at: '2025-01-08T10:15:00Z',
    whiteboard_data: null,
    triage_assessment: {
      reasoning: 'Mental health AI has significant potential but requires substantial detail about safety protocols and clinical validation',
      confidence: 0.9,
      refinement_questions: [
        {
          question: 'What specific safeguards and escalation protocols will you implement for crisis situations, including suicidal ideation detection?',
          field: 'what',
          why: 'Safety protocols are absolutely critical for any mental health application, and must be detailed before proceeding'
        },
        {
          question: 'Will this be positioned as a screening tool, ongoing support, or both? How will you ensure users understand the limitations of AI vs. professional therapy?',
          field: 'why',
          why: 'Clear positioning and user education are essential to prevent harm and manage expectations appropriately'
        },
        {
          question: 'What clinical validation studies or partnerships with mental health professionals would you pursue to establish credibility and effectiveness?',
          field: 'positive_examples',
          why: 'Clinical validation and professional endorsement are crucial for adoption and trust in mental health technology'
        }
      ]
    }
  },
  {
    id: 'idea-9',
    idea_id: 'idea-9',
    what: 'Peer-to-peer skill sharing platform where people teach each other practical skills',
    who: 'Adults wanting to learn practical skills like cooking, home repair, crafts without formal classes',
    why: 'Online courses are impersonal and expensive. This would connect people locally to teach and learn practical skills from each other.',
    what_expected: 'Create a community of skill sharing in every neighborhood',
    make_money: true,
    positive_examples: 'Airbnb for sharing, Udemy for learning, but in-person and peer-to-peer',
    negative_examples: 'YouTube tutorials lack personal interaction',
    status: 'market-demand-evaluated',
    created_at: '2025-01-07T15:30:00Z',
    updated_at: '2025-01-07T18:45:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-10',
    idea_id: 'idea-10',
    what: 'Smart home energy optimization system that learns usage patterns and automates savings',
    who: 'Homeowners wanting to reduce energy bills through automated optimization',
    why: 'Energy bills are rising but manual optimization is tedious. This system would learn patterns and automatically optimize for maximum savings.',
    what_expected: 'Reduce home energy costs by 25% through smart automation',
    make_money: true,
    positive_examples: 'Nest for thermostats, Tesla Powerwall for storage, but comprehensive optimization',
    negative_examples: 'Manual energy management is time-consuming and inconsistent',
    status: 'market-demand-evaluated',
    created_at: '2025-01-06T12:00:00Z',
    updated_at: '2025-01-06T14:30:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-11',
    idea_id: 'idea-11',
    what: 'Virtual reality fitness platform with gamified workouts and social competition',
    who: 'Fitness enthusiasts wanting engaging home workouts with social motivation',
    why: 'Home workouts are boring and lack motivation. VR fitness games could make exercise addictive while building community.',
    what_expected: 'Increase workout consistency by 80% through gamification',
    make_money: true,
    positive_examples: 'Peloton for community, Beat Saber for VR fitness, but comprehensive platform',
    negative_examples: 'Traditional home workouts lack engagement and community',
    status: 'market-saturation-evaluated',
    created_at: '2025-01-05T09:45:00Z',
    updated_at: '2025-01-05T11:20:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-12',
    idea_id: 'idea-12',
    what: 'AI-powered personal finance advisor that provides real-time spending insights and investment advice',
    who: 'Young professionals wanting to build wealth but lacking financial knowledge',
    why: 'Financial advisors are expensive and generic apps lack personalization. AI could provide personalized advice accessible to everyone.',
    what_expected: 'Help users increase savings by 40% and start investing confidently',
    make_money: true,
    positive_examples: 'Mint for tracking, Robinhood for investing, but with AI personalization',
    negative_examples: 'Generic budgeting apps don\'t provide actionable insights',
    status: 'market-saturation-evaluated',
    created_at: '2025-01-04T14:15:00Z',
    updated_at: '2025-01-04T16:50:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-13',
    idea_id: 'idea-13',
    what: 'Collaborative storytelling platform where readers can influence story direction through voting',
    who: 'Interactive fiction readers and aspiring writers wanting more engaging storytelling',
    why: 'Traditional books are passive experiences. This platform would let readers shape stories in real-time, creating unique narratives.',
    what_expected: 'Create a new form of interactive entertainment with higher engagement than traditional books',
    make_money: true,
    positive_examples: 'Choose Your Own Adventure books, Twitch Plays Pokemon for crowd participation',
    negative_examples: 'Traditional books don\'t adapt to reader preferences',
    status: 'usp-evaluated',
    created_at: '2025-01-03T11:30:00Z',
    updated_at: '2025-01-03T13:15:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-14',
    idea_id: 'idea-14',
    what: 'Smart parking solution that uses IoT sensors to guide drivers to available spots in real-time',
    who: 'Urban drivers frustrated with finding parking in busy city centers',
    why: 'Parking in cities wastes time and fuel. Smart sensors could guide drivers directly to open spots, reducing congestion and stress.',
    what_expected: 'Reduce parking search time by 70% and urban congestion by 15%',
    make_money: true,
    positive_examples: 'SpotHero for reservations, Google Maps for navigation, but real-time availability',
    negative_examples: 'Current parking apps show spots that are already taken',
    status: 'usp-evaluated',
    created_at: '2025-01-02T08:20:00Z',
    updated_at: '2025-01-02T10:45:00Z',
    whiteboard_data: null
  },
  {
    id: 'idea-validation-platform',
    idea_id: 'idea-validation-platform',
    what: 'I want to create a web application that helps users validate the market opportunity and commercial opportunity of their ideas. I want users to share their ideas via the web application, and be supported by these agents to help validate they meet a market opportunity and have commercial value and USP, as well commercial potential and revenue forecast (where the person wants to generate revenue from the idea). Also, where a person\'s idea idea is intended to have an online element, i want the agents to create user experience design concepts as well',
    who: 'I think that anyone who wants to either bring a service to market to make money, or have a positive impact on the world',
    why: 'Ideas come easy. What\'s much harder is the ability to critique those against market demand, competition, and commerciality to validate if there is a USP that an idea can provide, and a demand that it can fulfil. The barrier to entry in taking many ideas through a rigorous analysis process is too high for many people, and as such, providing a team of agents who people can collaborate with will open the market to new ideas and creations, and reduce the barrier to entry massively.',
    what_expected: 'I expect them to get strong evidence-based feedback on their ideas that helps them quickly drop ideas that have limited market opportunity, and drive forward rapidly ideas that strong market opportunity.',
    make_money: true,
    positive_examples: 'https://www.itonics-innovation.com https://sintra.ai https://ideabuddy.com https://www.bizplan.com https://www.qmarkets.net',
    negative_examples: 'None',
    status: 'triage',
    created_at: '2025-09-21T10:00:00Z',
    updated_at: '2025-09-21T10:45:00Z',
    whiteboard_data: null
  }
]

// Store for managing mock data state with localStorage persistence
const STORAGE_KEY = 'ideation_mock_data'
const DATA_VERSION = '2025-12-22-v2' // Orin in progress

function loadMockDataStore() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      // Check if we have the latest data version
      if (data.version === DATA_VERSION) {
        return data
      }
    }
  } catch (error) {
    console.warn('Failed to load mock data from localStorage:', error)
  }
  
  // Return fresh data with version
  const freshData = {
    ideas: [...MOCK_IDEAS],
    nextIdeaId: 4,
    version: DATA_VERSION
  }
  
  // Save the fresh data immediately
  saveMockDataStore(freshData)
  
  return freshData
}

function saveMockDataStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch (error) {
    console.warn('Failed to save mock data to localStorage:', error)
  }
}

let mockDataStore = loadMockDataStore()

class ApiService {
  constructor() {
    this.onAuthenticationFailure = null
    this.baseURL = API_BASE_URL
  }

  setAuthenticationFailureHandler(handler) {
    this.onAuthenticationFailure = handler
  }

  async request(endpoint, options = {}) {
    // Handle mock mode
    if (MOCK_MODE) {
      return this.mockRequest(endpoint, options)
    }

    // Ensure no double slashes by removing trailing slash from base URL
    const baseUrl = API_BASE_URL.replace(/\/$/, '')
    const url = `${baseUrl}${endpoint}`

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      // Include credentials (cookies) with every request
      credentials: 'include',
      ...options,
    }

    try {
      const response = await fetch(url, config)
      
      // Handle authentication failures
      if (response.status === 401 || response.status === 403) {
        console.log('Authentication failed, clearing token and logging out')
        if (this.onAuthenticationFailure) {
          this.onAuthenticationFailure()
        }
        throw new Error('Authentication failed - please log in again')
      }
      
      if (!response.ok) {
        // Try to get error message from response body
        let errorMessage = `HTTP error! status: ${response.status}`
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          // If response is not JSON, use default message
        }
        throw new Error(errorMessage)
      }

      return await response.json()
    } catch (error) {
      console.error('API request failed:', error)
      throw error
    }
  }

  // Mock API request handler
  async mockRequest(endpoint, options = {}) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 300))
    
    const method = options.method || 'GET'
    console.log(`[MOCK] ${method} ${endpoint}`)
    
    // Route to appropriate mock handler
    if (endpoint === '/ideas' && method === 'GET') {
      return this.mockGetIdeas()
    }
    
    if (endpoint === '/prompt' && method === 'POST') {
      return this.mockCreateIdea(JSON.parse(options.body))
    }
    
    if (endpoint.startsWith('/ideas/') && method === 'GET') {
      const ideaId = endpoint.split('/')[2]
      return this.mockGetIdea(ideaId)
    }
    
    if (endpoint.startsWith('/ideas/') && method === 'PUT') {
      const ideaId = endpoint.split('/')[2]
      return this.mockUpdateIdea(ideaId, JSON.parse(options.body))
    }
    
    if (endpoint.startsWith('/ideas/') && endpoint.includes('/history')) {
      const ideaId = endpoint.split('/')[2]
      return this.mockGetIdeaHistory(ideaId)
    }
    
    // Default mock response
    throw new Error(`Mock endpoint not implemented: ${method} ${endpoint}`)
  }

  // Create a new idea
  async createIdea(ideaData) {
    return this.request('/prompt', {
      method: 'POST',
      body: JSON.stringify(ideaData),
    })
  }

  // Send idea description to Auri for initial intake
  async auriIntake(intakeData) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1500))
      return {
        message: "I love this vision for helping entrepreneurs validate their ideas with AI-powered insights! Your platform could be a game-changer for founders looking to strengthen their business concepts before diving in. I've assembled a team to help bring this to life:",
        team_members: ["Orin", "Cleo", "Finn", "Una", "Vera"],
        next_steps: "Let's capture the details of your idea so the team can get started on the analysis."
      }
    }
    return this.request('/auri/intake', {
      method: 'POST',
      body: JSON.stringify(intakeData),
    })
  }

  async auriHelp(helpData) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      return {
        message: "I'm happy to help! This is a mock response. In production, I'll be able to answer your questions about using Opptora."
      }
    }
    return this.request('/auri/help', {
      method: 'POST',
      body: JSON.stringify(helpData),
    })
  }

  // Ask Orin about assessment
  async askOrin(askData) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      const score = askData.context?.assessment?.score || 7
      const responses = [
        `Based on my analysis, I'd recommend focusing on ${score >= 7 ? 'scaling and market validation' : score >= 5 ? 'strengthening your value proposition' : 'pivoting to address the core challenges'}.`,
        `Great question! The market research shows ${score >= 7 ? 'strong demand in your target segment' : score >= 5 ? 'moderate demand with room to differentiate' : 'you may need to reconsider your target audience'}.`,
        `Looking at the competition landscape, ${score >= 7 ? 'you have a solid unique advantage' : score >= 5 ? 'there are opportunities to stand out more' : 'you should focus on finding your niche'}.`
      ]
      return {
        message: responses[Math.floor(Math.random() * responses.length)]
      }
    }
    return this.request('/orin/ask', {
      method: 'POST',
      body: JSON.stringify(askData),
    })
  }

  // Auri feedback chat
  async auriFeedback(feedbackData) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 1000))
      const messageCount = (feedbackData.history || []).length
      if (messageCount >= 2) {
        return {
          message: "Thank you so much for sharing this feedback! I've captured it and our team will review it. Your input helps us make Opptora better!",
          feedback_id: 'mock-feedback-' + Date.now(),
          saved: true
        }
      }
      return {
        message: "That's really helpful! Could you tell me a bit more about what you'd like to see improved or changed?",
        feedback_id: feedbackData.feedback_id || 'mock-feedback-' + Date.now(),
        saved: false
      }
    }
    return this.request('/auri/feedback', {
      method: 'POST',
      body: JSON.stringify(feedbackData),
    })
  }

  // Admin: List all feedback
  async listFeedback(params = {}) {
    if (MOCK_MODE) {
      return {
        feedback: [],
        count: 0,
        has_more: false
      }
    }
    const queryString = new URLSearchParams(params).toString()
    return this.request(`/admin/feedback${queryString ? '?' + queryString : ''}`)
  }

  // Admin: Update feedback status
  async updateFeedbackStatus(feedbackId, status) {
    if (MOCK_MODE) {
      return {
        feedback_id: feedbackId,
        status: status,
        updated_at: new Date().toISOString()
      }
    }
    return this.request(`/admin/feedback/${feedbackId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
  }

  // Save help conversation when modal closes
  async saveHelpConversation(conversation) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        conversation_id: 'mock-help-' + Date.now(),
        summary: 'Help conversation summary',
        saved: true
      }
    }
    return this.request('/auri/help/save', {
      method: 'POST',
      body: JSON.stringify({ conversation }),
    })
  }

  // Admin: List all help conversations
  async listHelpConversations(params = {}) {
    if (MOCK_MODE) {
      return {
        conversations: [],
        count: 0,
        has_more: false
      }
    }
    const queryString = new URLSearchParams(params).toString()
    return this.request(`/admin/help-conversations${queryString ? '?' + queryString : ''}`)
  }

  // Admin: Update help conversation status
  async updateHelpConversationStatus(conversationId, status) {
    if (MOCK_MODE) {
      return {
        conversation_id: conversationId,
        status: status,
        updated_at: new Date().toISOString()
      }
    }
    return this.request(`/admin/help-conversations/${conversationId}`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    })
  }

  // Admin: Generate invite code
  async generateInvite(inviteData) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 500))
      const code = Math.random().toString(36).substring(2, 14).toUpperCase()
      return {
        codes: [code],
        count: 1,
        expires_at: new Date(Date.now() + (inviteData.expires_in || 168) * 3600000).toISOString()
      }
    }
    return this.request('/admin/invites/generate', {
      method: 'POST',
      body: JSON.stringify(inviteData),
    })
  }

  // Admin: List all invites
  async listInvites() {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        invites: [],
        count: 0
      }
    }
    return this.request('/admin/invites')
  }

  // Admin: List chase email logs
  async listChaseLogs(inviteCode = null) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        logs: [],
        count: 0
      }
    }
    const url = inviteCode ? `/admin/chase-logs?invite_code=${inviteCode}` : '/admin/chase-logs'
    return this.request(url)
  }

  // Admin: Send invite email
  async sendInviteEmail(code) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 500))
      return {
        message: 'Email sent successfully',
        code: code
      }
    }
    return this.request(`/admin/invites/${code}/send`, {
      method: 'POST',
    })
  }

  // Admin: Delete invite
  async deleteInvite(code) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        message: 'Invite deleted successfully',
        code: code
      }
    }
    return this.request('/admin/invites', {
      method: 'DELETE',
      body: JSON.stringify({ code }),
    })
  }

  // Beta signup (public, no auth required)
  async betaSignup(formData) {
    // Use the same base URL logic as other API calls
    const baseUrl = API_BASE_URL.replace(/\/$/, '')

    const response = await fetch(`${baseUrl}/beta-signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Contact form submission (public, no auth required)
  async sendContactEmail(formData) {
    const baseUrl = API_BASE_URL.replace(/\/$/, '')

    const response = await fetch(`${baseUrl}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(formData)
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  // Admin: Preview invite email
  async previewInviteEmail(code) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      // Return mock preview that matches the actual email format
      return {
        subject: 'Welcome to Opptora Beta - Your Invitation',
        body: `Hi there,

You've been invited to join Opptora Beta!

Opptora assembles a custom AI team for each of your ideas - from market opportunity analysis and go-to-market strategy, to business case development, UX design, and MVP building. Everything you need to go from idea to launch.

To sign up, visit: https://opptora.ai/register?code=${code}&email=test@example.com

As we're in beta, not all features are available yet. You can start today by working with Auri, your guide, and Orin, your opportunity analyst, to analyse market demand and competition, discover what makes your idea unique, and get an overall opportunity score with clear next steps. Plus, use the interactive canvas to develop your thinking, manage tasks, export detailed reports, and track all your ideas from one dashboard.

Coming soon, you'll meet the rest of your team: Cleo for go-to-market strategy, Finn for business case development, and for digital products - Una for UX design and Vera for MVP building. We'd love your input on what these specialists should deliver, so we can build what's truly useful for you.

Your voice matters - hit the feedback button anytime to share what's working, what's not, and what you'd love to see next.

Best,
The Opptora Team`,
        signup_url: `https://opptora.ai/register?code=${code}&email=test@example.com`,
        to: 'test@example.com',
        name: 'Test User'
      }
    }
    return this.request(`/admin/invites/${code}/preview`)
  }

  // Admin: List all users
  async listUsers() {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        users: [],
        count: 0
      }
    }
    return this.request('/admin/users')
  }

  // Admin: Disable/enable user
  async disableUser(username, disabled) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        message: disabled ? 'User disabled' : 'User enabled',
        username: username,
        disabled: disabled
      }
    }
    return this.request(`/admin/users/${username}/disable`, {
      method: 'POST',
      body: JSON.stringify({ disabled }),
    })
  }

  // Admin: Activate user (remove expiration)
  async activateUser(username) {
    if (MOCK_MODE) {
      await new Promise(resolve => setTimeout(resolve, 300))
      return {
        success: true,
        message: 'User activated',
        username: username
      }
    }
    return this.request('/admin/users/activate', {
      method: 'POST',
      body: JSON.stringify({ username }),
    })
  }

  // Get idea status
  async getIdeaStatus(ideaId) {
    return this.request(`/status/${ideaId}`)
  }

  // Update an idea
  async updateIdea(ideaId, ideaData) {
    return this.request(`/ideas/${ideaId}`, {
      method: 'PUT',
      body: JSON.stringify(ideaData),
    })
  }

  // Force progress idea to market research (bypass triage)
  async forceProgressIdea(ideaId) {
    return this.request(`/ideas/${ideaId}/force-progress`, {
      method: 'POST',
    })
  }

  // Get idea history
  async getIdeaHistory(ideaId) {
    return this.request(`/ideas/${ideaId}/history`)
  }

  // Get whiteboard data for a specific idea
  async getWhiteboard(ideaId) {
    if (MOCK_MODE) {
      return this.mockGetWhiteboard(ideaId)
    }
    return this.request(`/ideas/${ideaId}/whiteboard`)
  }

  // Save whiteboard data for a specific idea
  async saveWhiteboard(ideaId, whiteboardData) {
    if (MOCK_MODE) {
      return this.mockSaveWhiteboard(ideaId, whiteboardData)
    }
    return this.request(`/ideas/${ideaId}/whiteboard`, {
      method: 'POST',
      body: JSON.stringify({ whiteboard_data: whiteboardData }),
    })
  }

  // Delete whiteboard data for a specific idea (reset)
  async deleteWhiteboard(ideaId) {
    if (MOCK_MODE) {
      return this.mockDeleteWhiteboard(ideaId)
    }
    return this.request(`/ideas/${ideaId}/whiteboard`, {
      method: 'DELETE',
    })
  }

  // Poll for idea status updates
  async pollIdeaStatus(ideaId, onUpdate, maxAttempts = 30) {
    let attempts = 0
    
    const poll = async () => {
      try {
        const status = await this.getIdeaStatus(ideaId)
        onUpdate(status)
        
        if (status.status === 'triage' && attempts < maxAttempts) {
          attempts++
          setTimeout(poll, 2000) // Poll every 2 seconds
        }
      } catch (error) {
        console.error('Polling failed:', error)
        onUpdate({ error: error.message })
      }
    }
    
    poll()
  }

  // Get all ideas from the API
  async getIdeas() {
    return this.request('/ideas')
  }

  // Get a specific idea with full details
  async getIdea(ideaId) {
    return this.request(`/ideas/${ideaId}`)
  }

  // Assign team members to an idea (for legacy ideas without team_members)
  async assignTeamMembers(ideaId) {
    if (MOCK_MODE) {
      // In mock mode, return default team members
      return {
        idea_id: ideaId,
        team_members: ['Orin', 'Cleo', 'Finn'],
        message: 'Team assigned (mock)'
      }
    }
    return this.request(`/ideas/${ideaId}/assign-team`, {
      method: 'POST',
    })
  }

  // Get triage assessment for an idea
  async getTriageAssessment(ideaId) {
    if (MOCK_MODE) {
      return this.mockGetTriageAssessment(ideaId)
    }
    return this.request(`/ideas/${ideaId}/triage-assessment`)
  }

  // Send a chat message for idea creation
  async sendCreationChat(ideaId, messages) {
    if (MOCK_MODE) {
      return this.mockSendCreationChat(messages)
    }
    const payload = { idea_id: ideaId, messages }
    console.log('sendCreationChat - ideaId:', ideaId)
    console.log('sendCreationChat - messages:', messages)
    console.log('sendCreationChat - payload:', JSON.stringify(payload, null, 2))
    return this.request('/ideas/create-chat', {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Send a chat message for idea refinement
  async sendRefinementChat(ideaId, messages) {
    if (MOCK_MODE) {
      return this.mockSendRefinementChat(ideaId, messages)
    }
    const payload = { idea_id: ideaId, messages }
    console.log('sendRefinementChat - ideaId:', ideaId)
    console.log('sendRefinementChat - messages:', messages)
    console.log('sendRefinementChat - payload:', JSON.stringify(payload, null, 2))
    return this.request(`/ideas/${ideaId}/refine-chat`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  }

  // Login with username/email and password
  // Returns user info (token is set as HTTP-only cookie by the server)
  async login(credentials) {
    // Mock authentication for development
    if (MOCK_MODE) {
      return this.mockLogin(credentials)
    }

    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    })
  }

  // Register a new user with email and invite code
  // Returns user info (token is set as HTTP-only cookie by the server)
  async register(email, password, inviteCode) {
    // Mock registration for development
    if (MOCK_MODE) {
      return this.mockRegister({ email, password, invite_code: inviteCode })
    }

    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, invite_code: inviteCode }),
    })
  }

  // Logout - clears the auth cookie
  async logout() {
    if (MOCK_MODE) {
      // In mock mode, just clear local state
      localStorage.removeItem('authToken')
      localStorage.removeItem('userEmail')
      localStorage.removeItem('username')
      return { message: 'Logged out successfully' }
    }

    try {
      const response = await this.request('/auth/logout', {
        method: 'POST',
      })
      // Clear any local storage items related to auth
      localStorage.removeItem('userEmail')
      localStorage.removeItem('username')
      return response
    } catch (error) {
      // Even if the API call fails, clear local state
      localStorage.removeItem('userEmail')
      localStorage.removeItem('username')
      throw error
    }
  }

  // Mock login for development/testing
  async mockLogin(credentials) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    const { username, password } = credentials

    // Support both username and email for login
    // First try exact match (for email logins like admin@levantar.ai)
    // Then try username-only match
    let matchedUser = null
    let userEmail = null

    if (MOCK_USERS[username] && MOCK_USERS[username] === password) {
      // Exact match (e.g., admin@levantar.ai)
      matchedUser = username
      userEmail = username.includes('@') ? username : `${username}@example.com`
    } else {
      // Try matching by username part only
      const userIdentifier = username.includes('@') ? username.split('@')[0] : username
      if (MOCK_USERS[userIdentifier] && MOCK_USERS[userIdentifier] === password) {
        matchedUser = userIdentifier
        userEmail = username.includes('@') ? username : `${userIdentifier}@example.com`
      }
    }

    if (matchedUser) {
      // Generate a mock JWT with proper format (header.payload.signature)
      // The payload contains the email which is used by isAdminUser
      const payload = {
        user_id: `user-${matchedUser}`,
        username: matchedUser,
        email: userEmail,
        exp: Math.floor(Date.now() / 1000) + 3600
      }
      const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }))
      const body = btoa(JSON.stringify(payload))
      const mockToken = `${header}.${body}.mock-signature`

      return {
        token: mockToken,
        expires_in: 3600,
        user: {
          id: `user-${matchedUser}`,
          username: matchedUser,
          email: userEmail
        }
      }
    } else {
      throw new Error('Invalid credentials')
    }
  }

  // Mock register for development/testing
  async mockRegister({ email, password }) {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Check if email is already "registered"
    const username = email.split('@')[0]
    if (MOCK_USERS[username]) {
      throw new Error('Email already registered')
    }

    // "Register" the new user
    MOCK_USERS[username] = password

    // Generate a mock JWT-like token
    const mockToken = `mock-token-${username}-${Date.now()}`
    return {
      token: mockToken,
      expires_at: Date.now() + 3600000,
      email: email,
      username: `${username}_${Date.now().toString(36).slice(-8)}`
    }
  }

  // Mock API implementations
  async mockGetIdeas() {
    return {
      ideas: mockDataStore.ideas,
      count: mockDataStore.ideas.length
    }
  }

  async mockCreateIdea(ideaData) {
    const newIdea = {
      id: `idea-${mockDataStore.nextIdeaId}`,
      idea_id: `idea-${mockDataStore.nextIdeaId}`,
      ...ideaData,
      status: 'triage',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      whiteboard_data: null
    }
    
    mockDataStore.ideas.push(newIdea)
    mockDataStore.nextIdeaId++

    saveMockDataStore(mockDataStore)
    
    return { idea_id: newIdea.id }
  }

  async mockGetIdea(ideaId) {
    const idea = mockDataStore.ideas.find(i => i.id === ideaId || i.idea_id === ideaId)
    if (!idea) {
      throw new Error('Idea not found')
    }
    return idea
  }

  async mockGetTriageAssessment(ideaId) {
    const idea = mockDataStore.ideas.find(i => i.id === ideaId || i.idea_id === ideaId)
    if (!idea) {
      throw new Error('Idea not found')
    }
    if (!idea.triage_assessment) {
      return null
    }
    return idea.triage_assessment
  }

  async mockSendCreationChat(messages) {
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 400))
    
    const messageCount = messages.length
    const lastMessage = messages[messages.length - 1]
    const userMessage = lastMessage?.content || ''
    
    if (messageCount === 1) {
      return {
        message: "Let's begin - Please can you tell me about your idea?",
        timestamp: new Date().toISOString()
      }
    }
    
    if (messageCount >= 8) {
      return {
        message: JSON.stringify({
          action: "complete",
          idea_data: {
            what: userMessage.substring(0, 100),
            who: "Target audience based on conversation",
            why: "Problem and value proposition discussed",
            what_expected: "Expected outcomes from conversation",
            make_money: true,
            positive_examples: "Examples mentioned in conversation",
            negative_examples: "Pitfalls to avoid"
          },
          message: "Great work! I've captured all the details about your idea. It will now be submitted to our triage agents for evaluation to determine if it's ready for full market opportunity assessment. You'll be able to track the progress on your dashboard."
        }),
        timestamp: new Date().toISOString()
      }
    }
    
    const creationQuestions = [
      "Great! Now, who is this for? Tell me about your target audience.",
      "Perfect! Why does this matter? What problem does it solve?",
      "Excellent! What specific outcomes will your users experience?",
      "Will this be generating revenue? Yes or no?",
      "Can you share some positive examples of similar solutions done well?",
      "Are there any negative examples or pitfalls you want to avoid?",
      "Can you tell me a bit more about how this would work in practice?"
    ]
    
    const questionIndex = Math.min(messageCount - 2, creationQuestions.length - 1)
    
    return {
      message: creationQuestions[questionIndex],
      timestamp: new Date().toISOString()
    }
  }

  async mockSendRefinementChat(ideaId, messages) {
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 400))
    
    const lastMessage = messages[messages.length - 1]
    const userMessage = lastMessage?.content || ''
    
    const responses = [
      "That's a great point! Can you tell me more about your target market's specific pain points?",
      "Interesting! How do you envision this solving the problem differently than existing solutions?",
      "I see. What would make someone choose your solution over what they're currently using?",
      "Thanks for clarifying. Can you explain what success would look like for your users?",
      "That helps! How do you plan to reach your target audience initially?",
      "Good insight! What would be the key features that deliver the most value?"
    ]
    
    const randomResponse = responses[Math.floor(Math.random() * responses.length)]
    
    return {
      message: randomResponse,
      timestamp: new Date().toISOString()
    }
  }

  async mockUpdateIdea(ideaId, updateData) {
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }
    
    // Update the idea
    mockDataStore.ideas[ideaIndex] = {
      ...mockDataStore.ideas[ideaIndex],
      ...updateData,
      updated_at: new Date().toISOString()
    }
    
    // Save to localStorage
    saveMockDataStore(mockDataStore)
    
    console.log(`[MOCK] Updated idea ${ideaId}:`, {
      hasWhiteboardData: !!updateData.whiteboard_data,
      whiteboardKeys: updateData.whiteboard_data ? Object.keys(updateData.whiteboard_data) : []
    })
    
    return {
      idea_id: ideaId,
      message: 'Idea updated successfully',
      status: 'triage',
      updated_at: new Date().toISOString()
    }
  }

  async mockGetIdeaHistory(ideaId) {
    // Create timestamps for different time periods
    const now = new Date()
    
    // This week (2-5 days ago)
    const thisWeekDates = [
      new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      new Date(now.getTime() - 4 * 24 * 60 * 60 * 1000)  // 4 days ago
    ]
    
    // This month (10-25 days ago)
    const thisMonthDates = [
      new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000), // 10 days ago
      new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000), // 15 days ago
      new Date(now.getTime() - 20 * 24 * 60 * 60 * 1000)  // 20 days ago
    ]
    
    // Older (35-60 days ago)
    const olderDates = [
      new Date(now.getTime() - 35 * 24 * 60 * 60 * 1000), // 35 days ago
      new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000), // 45 days ago
      new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000)  // 60 days ago
    ]

    // Mock history data spanning different time periods
    const historyData = {
      status_history: [
        // This Week entries
        {
          new_status: 'opportunity-assessed',
          timestamp: thisWeekDates[0].toISOString(),
          reasoning: 'Final opportunity assessment completed with market analysis results'
        },
        {
          new_status: 'usp-evaluated',
          timestamp: thisWeekDates[1].toISOString(),
          reasoning: 'Unique Selling Proposition evaluation completed'
        },
        {
          new_status: 'market-saturation-evaluated',
          timestamp: thisWeekDates[2].toISOString(),
          reasoning: 'Market saturation analysis completed - competitive landscape assessed'
        },
        
        // This Month entries
        {
          new_status: 'market-demand-evaluated',
          timestamp: thisMonthDates[0].toISOString(),
          reasoning: 'Market demand evaluation completed with positive demand indicators'
        },
        {
          new_status: 'content-extraction-completed',
          timestamp: thisMonthDates[1].toISOString(),
          reasoning: 'Content extraction from web sources completed successfully'
        },
        {
          new_status: 'web-searches-completed',
          timestamp: thisMonthDates[2].toISOString(),
          reasoning: 'Web search execution completed for market research'
        },
        
        // Older entries
        {
          new_status: 'search-terms-generated',
          timestamp: olderDates[0].toISOString(),
          reasoning: 'Market research search terms generated successfully'
        },
        {
          new_status: 'opportunity-assessment',
          timestamp: olderDates[1].toISOString(),
          reasoning: 'Idea moved to opportunity assessment phase after successful triage'
        },
        {
          new_status: 'triage',
          timestamp: olderDates[2].toISOString(),
          reasoning: 'Idea submitted for initial AI evaluation and triage'
        }
      ]
    }
    
    return historyData
  }

  async mockGetWhiteboard(ideaId) {
    const idea = mockDataStore.ideas.find(i => i.id === ideaId || i.idea_id === ideaId)
    if (!idea) {
      throw new Error('Idea not found')
    }
    
    return {
      idea_id: ideaId,
      whiteboard_data: idea.whiteboard_data,
      updated_at: idea.updated_at
    }
  }

  async mockSaveWhiteboard(ideaId, whiteboardData) {
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }

    const now = new Date().toISOString()
    mockDataStore.ideas[ideaIndex].whiteboard_data = whiteboardData
    mockDataStore.ideas[ideaIndex].updated_at = now

    // Save to localStorage
    saveMockDataStore(mockDataStore)

    console.log(`[MOCK] Saved whiteboard for idea ${ideaId}:`, {
      hasWhiteboardData: !!whiteboardData,
      whiteboardKeys: whiteboardData ? Object.keys(whiteboardData) : []
    })

    return {
      success: true,
      idea_id: ideaId,
      updated_at: now
    }
  }

  async mockDeleteWhiteboard(ideaId) {
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }

    mockDataStore.ideas[ideaIndex].whiteboard_data = null

    // Save to localStorage
    saveMockDataStore(mockDataStore)

    console.log(`[MOCK] Deleted whiteboard for idea ${ideaId}`)

    return {
      success: true,
      idea_id: ideaId,
      message: 'Whiteboard data deleted successfully'
    }
  }

  // Task Management Methods
  async getTasks(ideaId) {
    if (MOCK_MODE) {
      return this.mockGetTasks(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/tasks`)
      return response
    } catch (error) {
      console.error('Error getting tasks:', error)
      throw error
    }
  }

  async createTask(taskData) {
    if (MOCK_MODE) {
      return this.mockCreateTask(taskData)
    }
    
    try {
      const response = await this.request('/tasks', {
        method: 'POST',
        body: JSON.stringify(taskData)
      })
      return response
    } catch (error) {
      console.error('Error creating task:', error)
      throw error
    }
  }

  async toggleTaskComplete(taskId, completed) {
    if (MOCK_MODE) {
      return this.mockToggleTaskComplete(taskId, completed)
    }
    
    try {
      const response = await this.request(`/tasks/${taskId}/complete`, {
        method: 'PUT',
        body: JSON.stringify({
          task_id: taskId,
          completed: completed
        })
      })
      return response
    } catch (error) {
      console.error('Error toggling task completion:', error)
      throw error
    }
  }

  async updateTask(taskId, taskData) {
    if (MOCK_MODE) {
      return this.mockUpdateTask(taskId, taskData)
    }
    
    try {
      const response = await this.request(`/tasks/${taskId}`, {
        method: 'PUT',
        body: JSON.stringify(taskData)
      })
      return response
    } catch (error) {
      console.error('Error updating task:', error)
      throw error
    }
  }

  async deleteTask(taskId) {
    if (MOCK_MODE) {
      return this.mockDeleteTask(taskId)
    }
    
    try {
      const response = await this.request(`/tasks/${taskId}`, {
        method: 'DELETE'
      })
      return response
    } catch (error) {
      console.error('Error deleting task:', error)
      throw error
    }
  }

  // Mock task methods
  async mockGetTasks(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const tasks = mockDataStore.tasks?.filter(task => task.idea_id === ideaId) || []
    
    return {
      tasks: tasks,
      count: tasks.length
    }
  }

  async mockCreateTask(taskData) {
    await new Promise(resolve => setTimeout(resolve, 300)) // Simulate API delay
    
    if (!mockDataStore.tasks) {
      mockDataStore.tasks = []
    }
    
    const taskId = `task-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const now = new Date().toISOString()
    
    const newTask = {
      task_id: taskId,
      idea_id: taskData.ideaId,
      title: taskData.title,
      description: taskData.description || '',
      completed: false,
      created_at: now,
      updated_at: now
    }
    
    mockDataStore.tasks.push(newTask)
    saveMockDataStore(mockDataStore)
    
    console.log('[MOCK] Created task:', newTask)
    
    return {
      task_id: taskId,
      message: 'Task created successfully'
    }
  }

  async mockToggleTaskComplete(taskId, completed) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    if (!mockDataStore.tasks) {
      mockDataStore.tasks = []
      return { message: 'Task not found' }
    }
    
    const taskIndex = mockDataStore.tasks.findIndex(task => task.task_id === taskId)
    if (taskIndex === -1) {
      throw new Error('Task not found')
    }
    
    const now = new Date().toISOString()
    mockDataStore.tasks[taskIndex].completed = completed
    mockDataStore.tasks[taskIndex].updated_at = now
    
    if (completed) {
      mockDataStore.tasks[taskIndex].completed_at = now
    } else {
      delete mockDataStore.tasks[taskIndex].completed_at
    }
    
    saveMockDataStore(mockDataStore)
    
    console.log('[MOCK] Updated task:', mockDataStore.tasks[taskIndex])
    
    return {
      message: completed ? 'Task marked as completed' : 'Task marked as incomplete'
    }
  }

  async mockUpdateTask(taskId, taskData) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    if (!mockDataStore.tasks) {
      mockDataStore.tasks = []
      throw new Error('Task not found')
    }
    
    const taskIndex = mockDataStore.tasks.findIndex(task => task.task_id === taskId)
    if (taskIndex === -1) {
      throw new Error('Task not found')
    }
    
    const now = new Date().toISOString()
    mockDataStore.tasks[taskIndex] = {
      ...mockDataStore.tasks[taskIndex],
      title: taskData.title || mockDataStore.tasks[taskIndex].title,
      description: taskData.description === undefined ? mockDataStore.tasks[taskIndex].description : taskData.description,
      updated_at: now
    }
    
    saveMockDataStore(mockDataStore)
    
    console.log('[MOCK] Updated task:', mockDataStore.tasks[taskIndex])
    
    return {
      message: 'Task updated successfully'
    }
  }

  async mockDeleteTask(taskId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    if (!mockDataStore.tasks) {
      mockDataStore.tasks = []
      throw new Error('Task not found')
    }
    
    const taskIndex = mockDataStore.tasks.findIndex(task => task.task_id === taskId)
    if (taskIndex === -1) {
      throw new Error('Task not found')
    }
    
    const deletedTask = mockDataStore.tasks[taskIndex]
    mockDataStore.tasks.splice(taskIndex, 1)
    
    saveMockDataStore(mockDataStore)
    
    console.log('[MOCK] Deleted task:', deletedTask)
    
    return {
      message: 'Task deleted successfully'
    }
  }

  // Pin/Unpin idea methods
  async pinIdea(ideaId) {
    if (MOCK_MODE) {
      return this.mockPinIdea(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/pin`, {
        method: 'POST'
      })
      return response
    } catch (error) {
      console.error('Error pinning idea:', error)
      throw error
    }
  }

  async unpinIdea(ideaId) {
    if (MOCK_MODE) {
      return this.mockUnpinIdea(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/pin`, {
        method: 'DELETE'
      })
      return response
    } catch (error) {
      console.error('Error unpinning idea:', error)
      throw error
    }
  }

  // Archive/Unarchive idea methods
  async archiveIdea(ideaId) {
    if (MOCK_MODE) {
      return this.mockArchiveIdea(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/archive`, {
        method: 'POST'
      })
      return response
    } catch (error) {
      console.error('Error archiving idea:', error)
      throw error
    }
  }

  async unarchiveIdea(ideaId) {
    if (MOCK_MODE) {
      return this.mockUnarchiveIdea(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/archive`, {
        method: 'DELETE'
      })
      return response
    } catch (error) {
      console.error('Error unarchiving idea:', error)
      throw error
    }
  }

  async getPinnedIdeas() {
    if (MOCK_MODE) {
      return this.mockGetPinnedIdeas()
    }
    
    try {
      const response = await this.request('/ideas/pinned')
      return response
    } catch (error) {
      console.error('Error getting pinned ideas:', error)
      throw error
    }
  }

  // Mock pinning methods
  async mockPinIdea(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }
    
    mockDataStore.ideas[ideaIndex].pinned = true
    mockDataStore.ideas[ideaIndex].pinned_at = new Date().toISOString()
    
    saveMockDataStore(mockDataStore)
    
    console.log(`[MOCK] Pinned idea ${ideaId}`)
    
    return {
      message: 'Idea pinned successfully',
      idea_id: ideaId
    }
  }

  async mockUnpinIdea(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }
    
    delete mockDataStore.ideas[ideaIndex].pinned
    delete mockDataStore.ideas[ideaIndex].pinned_at
    
    saveMockDataStore(mockDataStore)
    
    console.log(`[MOCK] Unpinned idea ${ideaId}`)
    
    return {
      message: 'Idea unpinned successfully',
      idea_id: ideaId
    }
  }

  // Mock archive methods
  async mockArchiveIdea(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }
    
    mockDataStore.ideas[ideaIndex].archived = true
    mockDataStore.ideas[ideaIndex].archived_at = new Date().toISOString()
    
    saveMockDataStore(mockDataStore)
    
    console.log(`[MOCK] Archived idea ${ideaId}`)
    
    return {
      message: 'Idea archived successfully',
      idea_id: ideaId
    }
  }

  async mockUnarchiveIdea(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const ideaIndex = mockDataStore.ideas.findIndex(i => i.id === ideaId || i.idea_id === ideaId)
    if (ideaIndex === -1) {
      throw new Error('Idea not found')
    }
    
    delete mockDataStore.ideas[ideaIndex].archived
    delete mockDataStore.ideas[ideaIndex].archived_at
    
    saveMockDataStore(mockDataStore)
    
    console.log(`[MOCK] Unarchived idea ${ideaId}`)
    
    return {
      message: 'Idea unarchived successfully',
      idea_id: ideaId
    }
  }

  async mockGetPinnedIdeas() {
    await new Promise(resolve => setTimeout(resolve, 200)) // Simulate API delay
    
    const pinnedIdeas = mockDataStore.ideas.filter(idea => idea.pinned)
    
    return {
      ideas: pinnedIdeas,
      count: pinnedIdeas.length
    }
  }

  // Report Management Methods
  async getReports(ideaId) {
    if (MOCK_MODE) {
      return this.mockGetReports(ideaId)
    }
    
    try {
      const response = await this.request(`/ideas/${ideaId}/reports`)
      return response
    } catch (error) {
      console.error('Error getting reports:', error)
      throw error
    }
  }

  async downloadReport(reportId, fileKey) {
    if (MOCK_MODE) {
      return this.mockDownloadReport(reportId, fileKey)
    }
    
    try {
      const response = await this.request(`/reports/${reportId}/download?file=${encodeURIComponent(fileKey)}`)
      return response
    } catch (error) {
      console.error('Error downloading report:', error)
      throw error
    }
  }

  // Mock report methods
  async mockGetReports(ideaId) {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const idea = mockDataStore.ideas.find(i => i.id === ideaId || i.idea_id === ideaId)
    if (!idea) {
      throw new Error('Idea not found')
    }
    
    if (idea.status === 'completed' && idea.opportunity_assessment) {
      return {
        reports: [
          {
            report_id: `report-${ideaId}-stage1`,
            type: 'stage_1_opportunity_assessment',
            filename: `${ideaId}_Stage1_Opportunity_Assessment.docx`,
            generated_at: idea.opportunity_assessment.generated_at || new Date().toISOString(),
            download_url: `/reports/report-${ideaId}-stage1/download?file=${ideaId}_Stage1.docx`,
            size: 45678
          }
        ],
        count: 1
      }
    }
    
    return {
      reports: [],
      count: 0
    }
  }

  async mockDownloadReport(reportId, fileKey) {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    return {
      success: true,
      message: 'Report download started - this would download a Word document in production'
    }
  }

  // Helper method to reset mock data (useful for testing)
  resetMockData() {
    if (MOCK_MODE) {
      localStorage.removeItem(STORAGE_KEY)
      mockDataStore = {
        ideas: [...MOCK_IDEAS],
        nextIdeaId: 3,
        tasks: [] // Initialize empty tasks array
      }
      saveMockDataStore(mockDataStore)
      console.log('[MOCK] Data reset to defaults')
    }
  }
}

export default new ApiService()